import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiCard, uiDocument, uiEmpty, uiGet, uiHeader, uiMeter, uiMoney, uiTrendChart } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { renderSuccession } from "./core/board.js";
import { cinemaIpoBell } from "../ui/cinema.js";

/**
 * Board — the boardroom, redesigned (replaces app.js renderBoard).
 *
 *  - The boardroom: five directors around the table, each lit by their mood
 *    (the current score of what they watch). Confidence and the next review
 *    in the centre.
 *  - "If the review were held today": each factor's score and weight, the
 *    score the Board is moving towards, and where confidence would land
 *    (the same scoring the quarterly review uses).
 *  - Address the Board: four topics, which worry each speaks to, and the
 *    expected effect. Speaking to their biggest worry lands; off-topic
 *    speeches mostly don't.
 *  - Confidence history, letters from the Chair, IPO readiness, succession.
 * Data: /api/board/desk (backend/board_desk.py). Actions: /api/actions/execute
 * (address_board, ipo).
 */

function bdMoodColor(m) { return m >= 65 ? "var(--sage)" : m >= 40 ? "var(--neon-gold)" : "var(--oxblood)"; }

function bdRoom(d) {
  const seats = d.directors.map((x, i) => `
    <div class="bd-seat bd-seat-${i}" title="${esc(x.cares_about)}">
      <div class="bd-dir" style="--mood:${bdMoodColor(x.mood)}">${esc(x.name.split(" ").map((p) => p[0]).join(""))}</div>
      <b>${esc(x.name)}</b><em>${esc(x.title)}</em><span style="color:${bdMoodColor(x.mood)}">${esc(x.mood_label)}</span>
    </div>`).join("");
  return `
    <div class="bd-room">
      <div class="bd-table">
        <div class="bd-centre"><div class="sx-eyebrow">Confidence in you</div><b>${d.confidence.toFixed(0)}</b><span>${esc(d.verdict)}</span>
          <em>Next review: month ${d.next_review_month} (${d.months_to_review} month${d.months_to_review === 1 ? "" : "s"})</em></div>
      </div>
      ${seats}
    </div>`;
}

function bdToday(d) {
  if (!d.today) return uiEmpty("No results yet", "The Board needs a month of numbers.");
  const t = d.today;
  return `
    <div class="bd-factors">${d.factors.map((f) => `
      <div class="bd-factor ${f.key === t.worry ? "worry" : ""}"><span>${esc(f.label)} <em>${Math.round(f.weight * 100)}%</em></span>
        <div><i style="width:${f.score}%; background:${bdMoodColor(f.score)}"></i></div><b>${Math.round(f.score)}</b></div>`).join("")}</div>
    <div class="bd-proj">
      <div><span>Today</span><b>${d.confidence.toFixed(0)}</b></div><i>→</i>
      <div><span>Moving towards</span><b>${t.target.toFixed(0)}</b></div><i>→</i>
      <div><span>At the next review</span><b class="${t.projected >= d.confidence ? "pos" : "neg"}">${t.projected.toFixed(0)}</b></div>
    </div>
    <p class="ma-note">Their biggest worry right now: <b>${esc(t.worry_label)}</b>.${t.cap ? ` ${esc(t.cap)}` : ""}</p>`;
}

window.renderBoard = async function (main, snap) {
  const d = await uiGet("/board/desk");
  const ipo = d.ipo;
  main.innerHTML = `
    <div class="sx bd">
      ${uiHeader("The Board", "Five directors judge you every quarter. See what they think, what they'll score, and speak to what worries them.")}
      ${d.intervention_streak ? `<div class="sx-finding crit"><div class="sx-finding-title">Crisis: ${d.intervention_streak} crisis-level review${d.intervention_streak > 1 ? "s" : ""} in a row</div>
        <div class="sx-finding-detail">Two in a row force mandatory cost cuts; three bring a vote of no confidence.</div></div>` : ""}
      ${bdRoom(d)}
      <div class="sx-grid-2">
        ${uiCard("If the review were held today", bdToday(d), { sub: "The same scoring the Board uses: seven factors, weighted" })}
        ${uiCard("Address the Board", `<div class="bd-topics">${d.topics.map((t) => `
          <button type="button" class="bd-topic ${t.hits_worry ? "hits" : ""}" data-focus="${esc(t.focus)}">
            ${t.hits_worry ? `<span class="bd-hit">Their biggest worry</span>` : ""}
            <b>${esc(t.focus)}</b><em>Speaks to ${t.speaks_to.map(esc).join(", ").toLowerCase()}</em>
            <span>Confidence +${t.gain_low}–${t.gain_high}</span></button>`).join("")}</div>
          ${d.spoke_recently ? `<p class="ma-note">You spoke to them recently: the effect is smaller this time.</p>` : ""}`,
          { sub: "A speech on what worries them lands; an off-topic one mostly doesn't" })}
      </div>
      <div class="sx-grid-2">
        ${uiCard("Confidence over time", d.history.length >= 2 ? uiTrendChart(d.history.map((h) => h.confidence), { labels: d.history.map((h) => `M${h.month}`), money: false, height: 160 })
          : uiEmpty("No reviews yet", "The first quarterly review is in month 3."), { sub: "One point per quarterly review" })}
        ${uiCard(d.is_public ? "Public company" : "Going public", d.is_public ? `<p>Listed since month ${d.ipo_month}.${d.activist_streak ? ` The market has missed its own revenue bar ${d.activist_streak} month${d.activist_streak > 1 ? "s" : ""} running: a third miss brings activist investors.` : ""}</p>` : `
          ${uiMeter(ipo.valuation, ipo.valuation_needed, { label: "Valuation the underwriters want" })}
          <div style="height:10px"></div>
          ${uiMeter(ipo.revenue, ipo.revenue_needed, { label: "Yearly revenue they want" })}
          <p class="ma-note">${ipo.ready ? `Ready. An IPO sells 20% new shares and raises about ${uiMoney(ipo.raise, { compact: true })}, diluting you.` : "Not ready yet."}</p>
          ${ipo.ready ? `<button type="button" class="primary" id="bd-ipo">Ring the bell: go public</button>` : ""}`)}
      </div>
      ${d.letters.length ? uiCard("Letters from the Chair", `${uiDocument(d.letters[0])}
        ${d.letters.length > 1 ? `<details class="bd-older"><summary>Earlier reviews (${d.letters.length - 1})</summary>${d.letters.slice(1).map(uiDocument).join("")}</details>` : ""}`) : ""}
      ${uiCard("Succession", `<div id="succession-panel">Loading…</div>`, { sub: "Who takes over when you step down" })}
      <p class="lx-note">${esc(d.rules)}</p>
    </div>`;
  main.querySelectorAll("[data-focus]").forEach((b) => b.addEventListener("click", async () => {
    try { toast((await postJSON("/actions/execute", { action: { type: "address_board", params: { focus: b.dataset.focus } } })).message); }
    catch (e) { toast(e.message); }
    await window.refreshAll();
  }));
  main.querySelector("#bd-ipo")?.addEventListener("click", async () => {
    if (!window.confirm(`Go public now? You'll raise about ${uiMoney(ipo.raise, { compact: true })} and own less of the company.`)) return;
    try {
      const r = await postJSON("/actions/execute", { action: { type: "ipo", params: {} } });
      if (/^IPO complete/.test(r.message || "")) await cinemaIpoBell({ name: (document.getElementById("db-company-name") || {}).textContent || "", message: r.message });
      else toast(r.message);
    } catch (e) { toast(e.message); }
    await window.refreshAll();
  });
  if (typeof renderSuccession === "function") renderSuccession().catch(() => {});
};
